import { useEffect, useRef, useState } from 'react';
import axiosPublic from '../../api/axiosPublic';

const resolveFallbackValue = (fallbackValue) =>
  typeof fallbackValue === 'function' ? fallbackValue() : fallbackValue;

const normalizeValue = (normalize, payload, fallbackValue) => {
  if (typeof normalize !== 'function') {
    return payload;
  }

  try {
    return normalize(payload, fallbackValue);
  } catch (error) {
    console.error('Failed to normalize website page payload:', error);
    return fallbackValue;
  }
};

export const useSilentWebsitePage = ({
  tenantKey,
  pageSlug,
  fallbackValue,
  normalize,
  enabled = true,
}) => {
  const fallbackRef = useRef(fallbackValue);
  const normalizeRef = useRef(normalize);
  fallbackRef.current = fallbackValue;
  normalizeRef.current = normalize;

  const [data, setData] = useState(() => resolveFallbackValue(fallbackValue));
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled && tenantKey));

  useEffect(() => {
    if (!enabled || !tenantKey || !pageSlug) {
      setLoading(false);
      setData(resolveFallbackValue(fallbackRef.current));
      setRawData(null);
      return;
    }

    let active = true;

    const fetchPage = async () => {
      const fallback = resolveFallbackValue(fallbackRef.current);
      setLoading(true);
      setData(fallback);
      setRawData(null);

      try {
        const response = await axiosPublic.get(`/api/${tenantKey}/pages/${pageSlug}`);
        if (!active) {
          return;
        }

        setRawData(response.data);
        setData(normalizeValue(normalizeRef.current, response.data, fallback));
      } catch (error) {
        console.error(`Error loading website page "${pageSlug}" for tenant "${tenantKey}":`, error);
        if (!active) {
          return;
        }

        setData(resolveFallbackValue(fallbackRef.current));
        setRawData(null);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchPage();

    return () => {
      active = false;
    };
  }, [enabled, pageSlug, tenantKey]);

  return {
    data,
    rawData,
    loading,
    setData,
    setRawData,
  };
};
